import type {
  ClientInitExtensions,
  ServerInitExtensions,
} from "../../setup/init-result.js";
import type { TelemetryPlan } from "../../setup/plan.js";

export interface InitExtrasContext {
  readonly plan: TelemetryPlan;
}

export interface InitExtrasContributor {
  buildClientExtras?(ctx: InitExtrasContext): ClientInitExtensions | undefined;
  buildServerExtras?(ctx: InitExtrasContext): ServerInitExtensions | undefined;
}

const contributors: InitExtrasContributor[] = [];

export function registerInitExtrasContributor(contributor: InitExtrasContributor): void {
  contributors.push(contributor);
}

function mergeClientTrees(partials: ClientInitExtensions[]): ClientInitExtensions {
  let sentry: ClientInitExtensions["sentry"];

  for (const partial of partials) {
    if (partial.sentry) {
      sentry = { ...sentry, ...partial.sentry };
    }
  }

  return sentry ? { sentry } : {};
}

function mergeServerTrees(partials: ServerInitExtensions[]): ServerInitExtensions {
  let sentry: ServerInitExtensions["sentry"];

  for (const partial of partials) {
    if (partial.sentry) {
      sentry = { ...sentry, ...partial.sentry };
    }
  }

  return sentry ? { sentry } : {};
}

export function mergeClientExtensions(plan: TelemetryPlan): ClientInitExtensions {
  const partials: ClientInitExtensions[] = [];

  for (const contributor of contributors) {
    const partial = contributor.buildClientExtras?.({ plan });
    if (partial && Object.keys(partial).length > 0) {
      partials.push(partial);
    }
  }

  return mergeClientTrees(partials);
}

export function mergeServerExtensions(plan: TelemetryPlan): ServerInitExtensions {
  const partials: ServerInitExtensions[] = [];

  for (const contributor of contributors) {
    const partial = contributor.buildServerExtras?.({ plan });
    if (partial && Object.keys(partial).length > 0) {
      partials.push(partial);
    }
  }

  return mergeServerTrees(partials);
}

/** @internal Test helper */
export function resetInitExtrasContributors(): void {
  contributors.length = 0;
}
